import { format } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import SSRIcon from "@ingka/ssr-icon";
import MoneyBill from "@ingka/ssr-icon/paths/money-bills";
import Text from "@ingka/text";
import Tooltip from "@ingka/tooltip";

import { css } from "../__generated-styled-system/css";
import {
  calculatePeriodBudgetFromFilteredData,
  filterBudgetDataForOrgNode,
} from "../features/budget/transforms";
import { useGetBudgetStatus } from "../features/budget/useGetBudgetStatus";
import { weekStartsOn$ } from "../features/dates/state";
import {
  formatDecimalHours,
  getWeeksInRange,
} from "../features/dates/transforms";
import { ORG_NODE_BY_ID, orgId$, selectedUnit$ } from "../features/org/state";
import { period$ } from "../features/period";

const wrapperStyles = css({
  display: "grid",
  gridTemplateColumns: "min-content 1fr",
  gridTemplateRows: "min-content 1fr",
  gridRowGap: "space100",
});

const periodPlanTextStyles = css({
  marginLeft: "space50!",
});

const textStyles = css({ gridColumn: "-1 / 1" });

export const PlanBudget: FC = () => {
  const { t } = useTranslation();
  const period = period$.value;
  const orgId = orgId$.value ?? "";
  const selectedUnit = selectedUnit$.value;
  const selectedNode = ORG_NODE_BY_ID.get(orgId);

  const { rawBudgetData: unitBudgetData, rawMappingsData: unitMappingsData } =
    useGetBudgetStatus({
      unitType: String(selectedUnit?.attributes?.["businessUnitType"] ?? ""),
      unitId: selectedUnit?.publicId ?? "",
      startDate: format(period[0], "yyyy-MM-dd"),
      endDate: format(period[1], "yyyy-MM-dd"),
    });

  const { filteredHours: budgetData } = filterBudgetDataForOrgNode(
    unitBudgetData ?? [],
    unitMappingsData ?? [],
    selectedNode,
  );

  const { min, max } = calculatePeriodBudgetFromFilteredData(
    budgetData,
    format(period[1], "yyyy-MM-dd"),
    format(period[0], "yyyy-MM-dd"),
  );

  const startDateForTotalWeeks = period[0];
  const endDateForTotalWeeks = period[1];
  const startDateForPerfectWeeks = period[0];
  const endDateForPerfectWeeks = period[1];

  const weekRangeForTotalWeeks = getWeeksInRange(
    startDateForTotalWeeks,
    endDateForTotalWeeks,
    weekStartsOn$.value,
  );

  const weekRangeForPerfectWeeks = getWeeksInRange(
    startDateForPerfectWeeks,
    endDateForPerfectWeeks,
    weekStartsOn$.value,
  );

  return (
    <div className={wrapperStyles}>
      <Text className={textStyles} tagName="p" textStyle="Body.S">
        {t("plan.periodBudget")}
      </Text>
      <SSRIcon paths={MoneyBill} />
      <Text
        className={periodPlanTextStyles}
        tagName="span"
        textStyle="Heading.XS"
      >
        <Tooltip
          className="budget-tableHeaderCell"
          describedById="tooltip-example"
          position="bottom"
          quickPreview
          tooltipText={
            min === max ? (
              <span style={{ fontWeight: "normal" }}>
                {t("plan.totalBudgetForWeek")} {weekRangeForTotalWeeks[0]}-
                {weekRangeForTotalWeeks[weekRangeForTotalWeeks.length - 1]}
              </span>
            ) : (
              <>
                <span>{formatDecimalHours(min)}</span>
                <span style={{ fontWeight: "normal" }}>
                  : {t("plan.budgetForWeek")} {weekRangeForPerfectWeeks[0]}-
                  {
                    weekRangeForPerfectWeeks[
                      weekRangeForPerfectWeeks.length - 1
                    ]
                  }
                </span>
                <br />
                <span>{formatDecimalHours(max)}</span>
                <span style={{ fontWeight: "normal" }}>
                  : {t("plan.budgetForWeek")} {weekRangeForTotalWeeks[0]}-
                  {weekRangeForTotalWeeks[weekRangeForTotalWeeks.length - 1]}
                </span>
              </>
            )
          }
          boundaryContainerId="tool-id"
        >
          <div>
            {min === max
              ? `${formatDecimalHours(max)}`
              : `${formatDecimalHours(min)} - ${formatDecimalHours(max)}`}
          </div>
        </Tooltip>
      </Text>
    </div>
  );
};
